/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import analytics.CategoryReductionWithAllocation
import analytics.GoalAllocation
import analytics.GoalSplit
import analytics.PortfolioAllocationPlan
import i18n.StringKey
import i18n.tr
import kotlinx.browser.document
import org.w3c.dom.HTMLElement
import kotlin.math.roundToInt

/**
 * Web component for AI-powered Recommendations screen
 * Shows unified portfolio allocation plan across all goals
 */
class WebRecommendationsComponent(private val navigationManager: WebNavigationManager) {
    private val viewModel = WebRecommendationsViewModel()
    private var containerElement: HTMLElement? = null

    init {
        viewModel.onStateChanged = {
            updateUI()
        }
    }

    fun render(): HTMLElement {
        val container = document.createElement("div") as HTMLElement
        containerElement = container

        container.innerHTML = """
            <div style="min-height: 100vh; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);">
                <!-- Header -->
                <div style="background: white; padding: 1.5rem; box-shadow: 0 2px 8px rgba(0,0,0,0.1); display: flex; align-items: center; justify-content: space-between;">
                    <div style="display: flex; align-items: center; gap: 1rem;">
                        <button id="backButton" style="background: none; border: none; cursor: pointer; font-size: 1.5rem; color: #667eea;">
                            ←
                        </button>
                        <h1 style="margin: 0; font-size: 1.5rem; color: #333;">${StringKey.RECO_TITLE.tr()}</h1>
                    </div>
                </div>

                <!-- Content -->
                <div style="padding: 2rem;">
                    <div id="recommendationsContainer" style="max-width: 1000px; margin: 0 auto;">
                        ${renderContent()}
                    </div>
                </div>
            </div>
        """.trimIndent()

        // Add event listeners
        container.querySelector("#backButton")?.addEventListener("click", {
            navigationManager.navigateTo(Screen.SavingsGoals(openCreateDialog = false))
        })

        return container
    }

    private fun renderContent(): String {
        return when {
            viewModel.isLoading -> renderLoadingState()
            viewModel.errorMessage != null -> renderErrorState(viewModel.errorMessage!!)
            viewModel.goals.isEmpty() -> renderEmptyState()
            viewModel.allocationPlan == null -> renderNoDataState()
            else -> renderUnifiedAllocationPlan(viewModel.allocationPlan!!)
        }
    }

    private fun renderLoadingState(): String {
        return """
            <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 4rem 2rem; text-align: center;">
                <div style="color: white; font-size: 1.5rem;">${StringKey.RECO_OPTIMIZING.tr()}</div>
            </div>
        """.trimIndent()
    }

    private fun renderErrorState(error: String): String {
        return """
            <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 4rem 2rem; text-align: center;">
                <div style="color: white; font-size: 1.2rem;">$error</div>
            </div>
        """.trimIndent()
    }

    private fun renderEmptyState(): String {
        return """
            <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 4rem 2rem; text-align: center;">
                <div style="font-size: 6rem; margin-bottom: 1.5rem; opacity: 0.3;">⭐</div>
                <h2 style="color: white; font-size: 1.75rem; margin-bottom: 1rem; font-weight: 600;">${StringKey.RECO_NO_GOALS_TITLE.tr()}</h2>
                <p style="color: rgba(255,255,255,0.9); font-size: 1rem; margin-bottom: 2rem; max-width: 400px;">
                    ${StringKey.RECO_NO_GOALS_DESC.tr()}
                </p>
            </div>
        """.trimIndent()
    }

    private fun renderNoDataState(): String {
        return """
            <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 4rem 2rem; text-align: center;">
                <div style="color: white; font-size: 1.2rem;">${StringKey.RECO_UNABLE_TO_GENERATE.tr()}</div>
            </div>
        """.trimIndent()
    }

    private fun renderUnifiedAllocationPlan(plan: PortfolioAllocationPlan): String {
        return """
            <!-- Plan Summary Card -->
            ${renderPlanSummary(plan)}

            <!-- Goal Allocations -->
            <div style="background: white; border-radius: 16px; padding: 1.5rem; margin-bottom: 1.5rem; box-shadow: 0 4px 12px rgba(0,0,0,0.1);">
                <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; color: #333; font-weight: bold;">
                    ${StringKey.RECO_MONTHLY_ALLOCATION.tr()}
                </h3>
                ${plan.goalAllocations.joinToString("") { renderGoalAllocation(it) }}
            </div>

            <!-- Spending Reductions with Goal Splits -->
            <div style="background: white; border-radius: 16px; padding: 1.5rem; box-shadow: 0 4px 12px rgba(0,0,0,0.1);">
                <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; color: #333; font-weight: bold;">
                    ${StringKey.RECO_SMART_SAVINGS.tr()}
                </h3>
                <p style="color: #666; font-size: 0.95rem; margin-bottom: 1.5rem;">
                    ${StringKey.RECO_SMART_SAVINGS_DESC.tr()}
                </p>
                ${plan.spendingReductions.joinToString("") { renderReductionWithSplit(it) }}
            </div>
        """.trimIndent()
    }

    private fun renderPlanSummary(plan: PortfolioAllocationPlan): String {
        val totalNeeded = plan.totalMonthlySavingsNeeded.toInt()
        val totalAvailable = plan.totalAvailableForSavings.toInt()
        val score = plan.optimizationScore.toInt()
        val isFeasible = plan.isFeasible
        val income = plan.forecastedMonthlyIncome.toInt()
        val expenses = plan.forecastedMonthlyExpenses.toInt()

        return """
            <div style="background: white; border-radius: 16px; padding: 1.5rem; margin-bottom: 1.5rem; box-shadow: 0 4px 12px rgba(0,0,0,0.1);">
                <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; color: #333; font-weight: bold;">
                    ${StringKey.RECO_PORTFOLIO_SUMMARY.tr()}
                </h3>

                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem; margin-bottom: 1rem;">
                    <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 12px; padding: 1rem; color: white;">
                        <div style="font-size: 0.85rem; opacity: 0.9; margin-bottom: 0.25rem;">${StringKey.RECO_TOTAL_NEEDED.tr()}</div>
                        <div style="font-size: 1.75rem; font-weight: bold;">$$totalNeeded</div>
                    </div>

                    <div style="background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%); border-radius: 12px; padding: 1rem; color: white;">
                        <div style="font-size: 0.85rem; opacity: 0.9; margin-bottom: 0.25rem;">${StringKey.RECO_AVAILABLE_MONTH.tr()}</div>
                        <div style="font-size: 1.75rem; font-weight: bold;">$$totalAvailable</div>
                    </div>

                    <div style="background: ${if (isFeasible) "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)" else "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"}; border-radius: 12px; padding: 1rem; color: white;">
                        <div style="font-size: 0.85rem; opacity: 0.9; margin-bottom: 0.25rem;">${StringKey.RECO_OPTIMIZATION_SCORE.tr()}</div>
                        <div style="font-size: 1.75rem; font-weight: bold;">$score%</div>
                    </div>
                </div>

                ${if (income > 0) """
                    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem; margin-bottom: 1rem;">
                        <div style="background: #e3f2fd; border-radius: 12px; padding: 1rem;">
                            <div style="font-size: 0.85rem; color: #1976d2; margin-bottom: 0.25rem;">${StringKey.RECO_FORECASTED_INCOME.tr()}</div>
                            <div style="font-size: 1.5rem; font-weight: bold; color: #1976d2;">$$income</div>
                        </div>
                        <div style="background: #fce4ec; border-radius: 12px; padding: 1rem;">
                            <div style="font-size: 0.85rem; color: #c2185b; margin-bottom: 0.25rem;">${StringKey.RECO_FORECASTED_EXPENSES.tr()}</div>
                            <div style="font-size: 1.5rem; font-weight: bold; color: #c2185b;">$$expenses</div>
                        </div>
                    </div>
                """ else ""}

                ${if (plan.message.isNotEmpty()) """
                    <div style="background: ${if (plan.fundingGap <= 0) "#e8f5e9" else "#fff3e0"}; border-left: 4px solid ${if (plan.fundingGap <= 0) "#4caf50" else "#ff9800"}; padding: 1rem; border-radius: 8px; white-space: pre-line;">
                        <div style="color: #333; font-size: 0.95rem; line-height: 1.6;">
                            ${plan.message}
                        </div>
                    </div>
                """ else if (!isFeasible) """
                    <div style="background: #fff3cd; border-left: 4px solid #ffc107; padding: 1rem; border-radius: 8px;">
                        <div style="color: #856404; font-weight: 600; margin-bottom: 0.25rem;">${StringKey.RECO_FUNDING_GAP.tr()}</div>
                        <div style="color: #856404; font-size: 0.9rem;">
                            ${StringKey.RECO_FUNDING_GAP_DESC.tr((totalNeeded - totalAvailable).toString())}
                        </div>
                    </div>
                """ else """
                    <div style="background: #d4edda; border-left: 4px solid #28a745; padding: 1rem; border-radius: 8px;">
                        <div style="color: #155724; font-weight: 600; margin-bottom: 0.25rem;">${StringKey.RECO_PLAN_FEASIBLE.tr()}</div>
                        <div style="color: #155724; font-size: 0.9rem;">
                            ${StringKey.RECO_PLAN_FEASIBLE_DESC.tr()}
                        </div>
                    </div>
                """}
            </div>
        """.trimIndent()
    }

    private fun renderGoalAllocation(allocation: GoalAllocation): String {
        val priorityColor = when (allocation.priority.name) {
            "MUST_HAVE", "CRITICAL" -> "#dc3545"
            "NICE_TO_HAVE", "HIGH" -> "#ffc107"
            "STRETCH_GOAL", "MEDIUM" -> "#17a2b8"
            else -> "#6c757d"
        }

        val priorityLabel = allocation.priority.name.replace("_", " ")
        val percentFunded = allocation.percentageOfNeed.roundToInt()

        return """
            <div style="background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%); border-radius: 12px; padding: 1rem; margin-bottom: 0.75rem; border-left: 4px solid $priorityColor;">
                <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.5rem;">
                    <div style="flex: 1;">
                        <div style="font-weight: 600; color: #333; font-size: 1rem; margin-bottom: 0.25rem;">
                            ${allocation.goalName}
                        </div>
                        <div style="font-size: 0.85rem; color: $priorityColor; font-weight: 600;">
                            $priorityLabel
                        </div>
                    </div>
                    <div style="text-align: right;">
                        <div style="font-size: 1.5rem; font-weight: bold; color: #667eea;">
                            $${allocation.monthlyAllocation.roundToInt()}
                        </div>
                        <div style="font-size: 0.85rem; color: #666;">
                            ${StringKey.RECO_PER_MONTH.tr()}
                        </div>
                    </div>
                </div>

                <div style="background: #e0e0e0; border-radius: 8px; height: 6px; overflow: hidden; margin-bottom: 0.5rem;">
                    <div style="background: $priorityColor; height: 100%; width: ${percentFunded}%; transition: width 0.3s;"></div>
                </div>

                <div style="display: flex; justify-content: space-between; font-size: 0.85rem; color: #666;">
                    <div>${if (allocation.isFullyFunded) StringKey.RECO_FULLY_FUNDED.tr() else StringKey.RECO_PERCENT_OF_NEED.tr(percentFunded.toString())}</div>
                    <div>${StringKey.RECO_COMPLETE_IN.tr()} ${if (allocation.projectedCompletionMonths > 100) StringKey.RECO_COMPLETE_NEVER.tr() else StringKey.RECO_COMPLETE_MONTHS.tr(allocation.projectedCompletionMonths.toString())}</div>
                </div>
            </div>
        """.trimIndent()
    }

    private fun renderReductionWithSplit(reductionWithAllocation: CategoryReductionWithAllocation): String {
        val reduction = reductionWithAllocation.reduction
        val splits = reductionWithAllocation.goalSplits
        val categoryName = reduction.category.replace("_", " ")
        val reductionPercent = (reduction.reductionPercentage * 100).roundToInt()

        return """
            <div style="background: linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%); border-radius: 12px; padding: 1.25rem; margin-bottom: 1rem;">
                <div style="display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 1rem;">
                    <div style="flex: 1;">
                        <div style="font-weight: 700; color: #333; font-size: 1.05rem; margin-bottom: 0.25rem;">
                            $categoryName
                        </div>
                        <div style="color: #666; font-size: 0.9rem; margin-bottom: 0.5rem;">
                            ${reductionWithAllocation.rationale}
                        </div>
                        <div style="display: flex; gap: 1rem; font-size: 0.85rem;">
                            <div style="color: #999;">
                                ${StringKey.RECO_CURRENT.tr(reduction.currentMonthlySpending.roundToInt().toString())}
                            </div>
                            <div style="color: #667eea; font-weight: 600;">
                                →  $${reduction.recommendedMonthlySpending.roundToInt()}/mo
                            </div>
                        </div>
                    </div>
                    <div style="flex-shrink: 0; text-align: right;">
                        <div style="font-size: 1.5rem; font-weight: bold; color: #dc3545;">
                            -$${reduction.suggestedReduction.roundToInt()}
                        </div>
                        <div style="font-size: 0.85rem; color: #dc3545;">
                            ${StringKey.RECO_CUT.tr(reductionPercent.toString())}
                        </div>
                    </div>
                </div>

                ${if (splits.isNotEmpty()) """
                    <div style="border-top: 1px solid rgba(0,0,0,0.1); padding-top: 0.75rem;">
                        <div style="font-size: 0.85rem; color: #666; margin-bottom: 0.5rem; font-weight: 600;">
                            ${StringKey.RECO_ALLOCATION_ACROSS_GOALS.tr()}
                        </div>
                        ${splits.joinToString("") { renderGoalSplit(it) }}
                    </div>
                """ else ""}
            </div>
        """.trimIndent()
    }

    private fun renderGoalSplit(split: GoalSplit): String {
        val percentage = split.percentage.roundToInt()

        return """
            <div style="display: flex; align-items: center; gap: 0.75rem; padding: 0.5rem; background: white; border-radius: 8px; margin-bottom: 0.5rem;">
                <div style="flex: 1; display: flex; align-items: center; gap: 0.5rem;">
                    <div style="width: 8px; height: 8px; background: #667eea; border-radius: 50%;"></div>
                    <div style="font-size: 0.9rem; color: #333; font-weight: 500;">
                        ${split.goalName}
                    </div>
                </div>
                <div style="font-size: 0.9rem; font-weight: 600; color: #667eea; min-width: 60px; text-align: right;">
                    $${split.amount.roundToInt()}/mo
                </div>
                <div style="font-size: 0.85rem; color: #999; min-width: 40px; text-align: right;">
                    $percentage%
                </div>
            </div>
        """.trimIndent()
    }

    private fun updateUI() {
        val container = containerElement?.querySelector("#recommendationsContainer") as? HTMLElement ?: return
        container.innerHTML = renderContent()
    }
}
